import React, {
  useCallback,
  type PropsWithChildren,
  useState,
  useEffect,
  useRef,
} from 'react';
import {
  useInRouterContext,
  useLocationLegacy as useLocation,
} from '@leagueplatform/routing';
import { ImpersonationType } from '@leagueplatform/auth-utils';
import { PermissionsErrorModal } from './permissions-error-modal.component';

const stateSetters = {
  setMutationWasReported: (() => {}) as React.Dispatch<
    React.SetStateAction<boolean>
  >,
  setErrorWasReported: (() => {}) as React.Dispatch<
    React.SetStateAction<boolean>
  >,
  setIsPermissionError: (() => {}) as React.Dispatch<
    React.SetStateAction<boolean>
  >,
  setImpersonationType: (() => {}) as React.Dispatch<
    React.SetStateAction<ImpersonationType>
  >,
};

export const reportAgentPermissionError = (
  reportOnlyOnce?: boolean,
  permissionsErrorMessage?: boolean,
  impersonationType?: ImpersonationType,
) => {
  if (reportOnlyOnce) {
    stateSetters.setMutationWasReported(true);
  } else {
    stateSetters.setErrorWasReported(true);
  }
  if (permissionsErrorMessage) {
    stateSetters.setIsPermissionError(true);
  }
  if (impersonationType) {
    stateSetters.setImpersonationType(impersonationType);
  }
};

const PermissionsErrorModalProviderInner = ({
  pathname,
  children,
}: PropsWithChildren<{ pathname: string }>) => {
  const [errorWasReported, setErrorWasReported] = useState(false);
  const [mutationWasReported, setMutationWasReported] = useState(false);
  const [isPermissionsError, setIsPermissionError] = useState(false);
  const [impersonationType, setImpersonationType] =
    useState<ImpersonationType>(null);

  const initialPathname = useRef(pathname);

  stateSetters.setErrorWasReported = setErrorWasReported;
  stateSetters.setMutationWasReported = setMutationWasReported;
  stateSetters.setIsPermissionError = setIsPermissionError;
  stateSetters.setImpersonationType = setImpersonationType;

  const onModalOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setMutationWasReported(false);
      setErrorWasReported(false);
    }
  }, []);

  const shouldShowModal = mutationWasReported || errorWasReported;

  useEffect(() => {
    const unregister = () => {
      setErrorWasReported(false);
      setIsPermissionError(false);
    };

    if (pathname !== initialPathname.current) {
      unregister();
    }

    return () => {
      unregister();
    };
  }, [pathname]);

  return (
    <>
      {children}
      {shouldShowModal && (
        <PermissionsErrorModal
          onModalOpenChange={onModalOpenChange}
          isActionError={mutationWasReported && !isPermissionsError}
          impersonationType={impersonationType}
        />
      )}
    </>
  );
};

const InRouterWrapper = ({ children }: PropsWithChildren<{}>) => {
  const { pathname } = useLocation();

  return (
    <PermissionsErrorModalProviderInner pathname={pathname}>
      {children}
    </PermissionsErrorModalProviderInner>
  );
};

export const PermissionsErrorModalProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const isInRouter = useInRouterContext();

  if (isInRouter) {
    return <InRouterWrapper>{children}</InRouterWrapper>;
  }
  return (
    <PermissionsErrorModalProviderInner pathname="">
      {children}
    </PermissionsErrorModalProviderInner>
  );
};
